"use client";

import { Link, useMatchRoute } from "@tanstack/react-router";
import { ChevronRightIcon } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
	Sidebar,
	SidebarContent,
	SidebarFooter,
	SidebarGroup,
	SidebarGroupLabel,
	SidebarHeader,
	SidebarMenu,
	SidebarMenuButton,
	SidebarMenuItem,
	SidebarMenuSub,
	SidebarMenuSubButton,
	SidebarMenuSubItem,
	SidebarRail,
	useSidebar,
} from "@/components/ui/sidebar";
import { cn, type SafeOmit } from "@/lib/utils";
import { SubscriptionTracker } from "@/modules/settings/subscription-tracker";
import { BranchSwitcher } from "./BranchSwitcher";
import { navigation, paths } from "./config";
import { SidebarProfileMenu } from "./SidebarProfileMenu";
import type { NavigationGroup, NavigationItem } from "./types";

const NavSubItem = ({
	subItem,
	setOpenMobile,
}: {
	subItem: SafeOmit<NavigationItem, "items">;
	setOpenMobile: (open: boolean) => void;
}) => {
	"use no memo";

	const { t } = useTranslation();
	const match = useMatchRoute();
	const path = paths[subItem.module];
	const Icon = subItem.icon;

	return (
		<SidebarMenuSubItem>
			<SidebarMenuSubButton asChild isActive={!!match({ to: path })}>
				<Link onClick={() => setOpenMobile(false)} to={path}>
					{Icon && <Icon className="mr-2 size-4" />}
					<span>{t(($) => $.common.components.sidebar.items[subItem.module])}</span>
					{subItem.badge && <span className="ml-auto rounded-full px-1 py-0 text-xs">{subItem.badge}</span>}
				</Link>
			</SidebarMenuSubButton>
		</SidebarMenuSubItem>
	);
};

const NavItem = ({
	item,
	openGroups,
	toggleGroup,
	setOpenMobile,
}: {
	item: NavigationItem;
	openGroups: Record<string, boolean>;
	toggleGroup: (title: string, value?: boolean) => void;
	setOpenMobile: (open: boolean) => void;
}) => {
	"use no memo";

	const { t } = useTranslation();
	const match = useMatchRoute();
	const Icon = item.icon;

	// Simple Link Item
	if (!item.items) {
		const path = paths[item.module];
		return (
			<SidebarMenuItem>
				<SidebarMenuButton
					asChild
					isActive={!!match({ to: path })}
					tooltip={t(($) => $.common.components.sidebar.items[item.module])}
				>
					<Link onClick={() => setOpenMobile(false)} to={path}>
						{Icon && <Icon className="mr-2 size-4" />}
						<span>{t(($) => $.common.components.sidebar.items[item.module])}</span>
						{item.badge && <span className="ml-auto rounded-full px-1 py-0 text-xs">{item.badge}</span>}
					</Link>
				</SidebarMenuButton>
			</SidebarMenuItem>
		);
	}

	// Collapsible Group Item
	const isActive = !!match({ to: item.url });
	const groupActive = item.items.some((sub) => !!match({ to: paths[sub.module] }));
	const isCollapsibleOpen = openGroups[item.module] ?? (groupActive || isActive);

	return (
		<Collapsible onOpenChange={(open) => toggleGroup(item.module, open)} open={isCollapsibleOpen}>
			<SidebarMenuItem>
				<CollapsibleTrigger asChild>
					<SidebarMenuButton
						asChild
						isActive={isActive}
						tooltip={t(($) => $.common.components.sidebar.items[item.module])}
					>
						<div className="group flex w-full items-center">
							<Link className="flex flex-1 items-center" onClick={() => setOpenMobile(false)} to={item.url}>
								{Icon && <Icon className="mr-2 size-4" />}
								<span>{t(($) => $.common.components.sidebar.items[item.module])}</span>
								{item.badge && <span className="ml-auto rounded-full px-1 py-0 text-xs">{item.badge}</span>}
							</Link>
							{/* Fix: Moved Chevron out of the Link to avoid button-in-anchor error */}
							<ChevronRightIcon
								className={cn("ml-auto size-4 transition-transform duration-200", isCollapsibleOpen && "rotate-90")}
							/>
						</div>
					</SidebarMenuButton>
				</CollapsibleTrigger>
				<CollapsibleContent>
					<SidebarMenuSub>
						{item.items.map((subItem) => (
							<NavSubItem key={subItem.module} setOpenMobile={setOpenMobile} subItem={subItem} />
						))}
					</SidebarMenuSub>
				</CollapsibleContent>
			</SidebarMenuItem>
		</Collapsible>
	);
};

const NavGroup = ({
	group,
	openGroups,
	toggleGroup,
	setOpenMobile,
}: {
	group: NavigationGroup;
	openGroups: Record<string, boolean>;
	toggleGroup: (title: string, value?: boolean) => void;
	setOpenMobile: (open: boolean) => void;
}) => {
	const { t } = useTranslation();

	return (
		<SidebarGroup>
			<SidebarGroupLabel>{t(($) => $.common.components.sidebar.groups[group.title])}</SidebarGroupLabel>
			<SidebarMenu>
				{group.items.map((item) => (
					<NavItem
						item={item}
						key={item.module}
						openGroups={openGroups}
						setOpenMobile={setOpenMobile}
						toggleGroup={toggleGroup}
					/>
				))}
			</SidebarMenu>
		</SidebarGroup>
	);
};

const NavigationContent = ({
	className,
	setOpenMobile,
	openGroups,
	toggleGroup,
}: {
	className?: string;
	setOpenMobile: (open: boolean) => void;
	openGroups: Record<string, boolean>;
	toggleGroup: (title: string, value?: boolean) => void;
}) => {
	return (
		<div className={cn("flex flex-col gap-4", className)}>
			{navigation.groups.map((group) => (
				<NavGroup
					group={group}
					key={group.title}
					openGroups={openGroups}
					setOpenMobile={setOpenMobile}
					toggleGroup={toggleGroup}
				/>
			))}
		</div>
	);
};

function NavigationSection() {
	const { setOpenMobile } = useSidebar();
	const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});

	const toggleGroup = (title: string, value?: boolean) => {
		setOpenGroups((prev) => ({
			...prev,
			[title]: value ?? !prev[title],
		}));
	};

	return <NavigationContent openGroups={openGroups} setOpenMobile={setOpenMobile} toggleGroup={toggleGroup} />;
}

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
	return (
		<Sidebar collapsible="icon" variant="floating" {...props}>
			<SidebarHeader>
				<BranchSwitcher />
			</SidebarHeader>

			<SidebarContent>
				<NavigationSection />
			</SidebarContent>

			<SidebarFooter>
				<SidebarSubscriptionTracker />

				<SidebarProfileMenu />
			</SidebarFooter>

			<SidebarRail />
		</Sidebar>
	);
}

const SidebarSubscriptionTracker = () => {
	const { state } = useSidebar();

	return <SubscriptionTracker isExpanded={state === "expanded"} />;
};
